import * as React from 'react';
import { compareAsc, format } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '../common/Popover';
import { isDateRange } from 'react-day-picker';
import { ReactDayPickerProps, ReactDayPicker } from './ReactDayPicker';
import { useTranslation } from 'react-i18next';
import { getLocaleDateFns } from '@/utils/language';
import { Button } from '../common/Button';
import { cn } from '@/utils/utils';
import { DATE_YMD } from '@/configs/constants';

type CalendarProps = ReactDayPickerProps & {
    mode: 'single' | 'multiple' | 'range',
    selected: any,
    onSelect: any,
    type?: 'calendar' | 'datePicker',
    placeholder?: string,
    useIconRemove?: boolean,
    calendarDisabled?: boolean,
    required?: boolean,
    isInvalid?: boolean
};

export default function Calendar({
    type = 'datePicker',
    mode,
    selected,
    onSelect,
    onDayClick,
    placeholder = 'placeholder.select_date',
    useIconRemove = true,
    calendarDisabled = false,
    required = false,
    isInvalid = false,
    ...props
}: CalendarProps) {
    // hooks
    const { t, i18n: { language } } = useTranslation();
    const localeDateFns = getLocaleDateFns(language);

    // state
    const [openCalendar, setOpenCalendar] = React.useState<boolean>(false);
    const [countClickDays, setCountClickDays] = React.useState<number>(0);

    // function
    const onCheckCloseCalendarRange = (date: Date) => {
        if (countClickDays > 0 && selected?.from && compareAsc(selected.from, date) !== 0) {
            setOpenCalendar(false);
            setCountClickDays(0);
        } else {
            setCountClickDays(state => state + 1);
        }
    };

    // render
    const CalendarChildren = () => {
        const defaultMonth = mode === 'single' ?
            selected :
            (
                mode === 'range' ?
                    (selected?.from ?? selected?.to) :
                    (
                        mode === 'multiple' ? selected?.[selected?.length - 1] : undefined
                    )
            );

        return (
            <ReactDayPicker
                mode={mode}
                locale={localeDateFns}
                selected={selected}
                onSelect={onSelect}
                onDayClick={(date, modifiers, e) => {
                    onDayClick && onDayClick(date, modifiers, e);
                    mode === 'single' && setOpenCalendar(false);
                    mode === 'range' && onCheckCloseCalendarRange(date);
                }}
                defaultMonth={defaultMonth}
                required={required}
                {...props}
            />
        );
    };

    if (type === 'calendar') {
        return <CalendarChildren />;
    }

    return (
        <Popover open={openCalendar} onOpenChange={setOpenCalendar}>
            <PopoverTrigger asChild>
                <Button
                    disabled={calendarDisabled}
                    variant='input'
                    className={cn(
                        'relative justify-start text-left font-normal',
                        'min-w-[160px] w-full',
                        !selected && required && 'border-primaryWeb',
                        !selected && 'text-placeholder',
                        selected && useIconRemove && 'pr-9',
                        isInvalid && 'border-destructive'
                    )}
                >
                    <CalendarIcon className='mr-2 h-4 w-4' />
                    {
                        selected && selected instanceof Date && format(selected, DATE_YMD)
                    }
                    {
                        (selected instanceof Array && selected.length > 0) && (
                            selected.length > 3 ?
                                `${selected.length} selected date` :
                                selected.map(selectDate => format(selectDate, DATE_YMD)).join(', '))
                    }
                    {
                        selected && isDateRange(selected) && selected.from &&
                        `${format(selected.from, DATE_YMD)} ~ ${selected.to ? format(selected.to, DATE_YMD) : ''}`
                    }
                    {
                        (!selected || (selected instanceof Array && selected.length === 0)) && (
                            <span>{t(`${placeholder}`)}</span>
                        )
                    }
                    {
                        selected && useIconRemove && !calendarDisabled && (
                            <X
                                className='h-4 w-4 text-icon-1 icon-right-input right-4'
                                onPointerDown={() => {
                                    onSelect && onSelect(undefined);
                                    mode === 'range' && setCountClickDays(0);
                                }}
                            />
                        )
                    }
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0 z-[1100]'>
                <CalendarChildren />
            </PopoverContent>
        </Popover>
    );
}
